import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Loader2, RefreshCw, AlertCircle, Search, X, ChevronUp, ChevronDown, Activity, CheckCircle, XCircle, ShieldCheck, Clock, BarChart2, Calendar } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../../components/Navigation';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import { getErrorMessage } from '../../services/apiService';
import { getAllUsersAdmin, getActivitySummary, AdminUserRow, UserActivitySummary } from '../../services/userActivityService';

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

const relativeTime = (iso?: string): string => {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const fmtDate = (iso?: string): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const roleBadge = (row: AdminUserRow) => {
  if (row.isSystemAdmin) return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700">SystemAdmin</span>;
  if (row.role === 'ClientAdmin') return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700">ClientAdmin</span>;
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">User</span>;
};

type SortField = 'name' | 'client' | 'lastLogin' | 'apiRequests';
type SortDir = 'asc' | 'desc';

// --------------------------------------------------------------------------
// Period options
// --------------------------------------------------------------------------

interface PeriodOption {
  label: string;
  startDate: string;
  endDate: string;
}

function buildPeriodOptions(): PeriodOption[] {
  const now = new Date();
  const options: PeriodOption[] = [];
  for (let i = 0; i < 4; i++) {
    const year = now.getMonth() - i < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = ((now.getMonth() - i) % 12 + 12) % 12;
    const start = new Date(Date.UTC(year, month, 1, 0, 0, 0));
    const end = i === 0
      ? new Date()
      : new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
    options.push({
      label: start.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    });
  }
  return options;
}

const PERIOD_OPTIONS = buildPeriodOptions();

// --------------------------------------------------------------------------
// Activity detail modal
// --------------------------------------------------------------------------

interface ActivityModalProps {
  row: AdminUserRow;
  period: PeriodOption;
  onClose: () => void;
}

const ActivityModal: React.FC<ActivityModalProps> = ({ row, period, onClose }) => {
  const [summary, setSummary] = useState<UserActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setSummary(null);
    setError('');
    getActivitySummary(row.id, period.startDate, period.endDate)
      .then(setSummary)
      .catch((err) => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [row.id, period.startDate, period.endDate]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08]">
          <div>
            <h2 className="text-base font-semibold text-[#0f172a]">{row.firstName} {row.lastName}</h2>
            <p className="text-sm text-[#64748b]">{row.email} · {row.clientName ?? 'No client'}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/[0.05] transition-colors">
            <X className="w-4 h-4 text-[#64748b]" />
          </button>
        </div>

        <div className="px-6 py-5">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#c41e3a]" />
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
          {summary && (
            <div className="space-y-5">
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Total Logins', value: summary.totalLogins.toLocaleString(), icon: CheckCircle, color: 'text-green-600' },
                  { label: 'Failed (30d)', value: summary.failedLoginsLast30Days.toLocaleString(), icon: XCircle, color: 'text-red-500' },
                  { label: `Billable Lookups (${period.label})`, value: summary.apiRequestsLast30Days.toLocaleString(), icon: BarChart2, color: 'text-blue-600' },
                  { label: 'Active API Keys', value: summary.activeApiKeys.toLocaleString(), icon: ShieldCheck, color: 'text-purple-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-[#f8fafc] rounded-xl p-3 text-center">
                    <s.icon className={`w-4 h-4 mx-auto mb-1 ${s.color}`} />
                    <div className="text-lg font-bold text-[#0f172a]">{s.value}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Last login */}
              <div className="bg-[#f8fafc] rounded-xl px-4 py-3 flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#94a3b8] shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#94a3b8]">Last login</div>
                  <div className="text-sm font-medium text-[#0f172a]">
                    {summary.lastLoginAt ? fmtDate(summary.lastLoginAt) : 'Never'}
                    {summary.lastLoginAt && <span className="ml-2 text-[#64748b]">({relativeTime(summary.lastLoginAt)})</span>}
                  </div>
                </div>
              </div>

              {/* Recent logins */}
              {summary.recentLogins.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8] mb-2">Recent Login Attempts</h3>
                  <div className="rounded-xl border border-black/[0.08] overflow-hidden divide-y divide-black/[0.05]">
                    {summary.recentLogins.map((l) => (
                      <div key={l.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <div className="flex items-center gap-2">
                          {l.success
                            ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                          <span className="text-[#0f172a]">{l.success ? 'Success' : (l.failureReason ?? 'Failed')}</span>
                          {l.ipAddress && <span className="text-[#94a3b8] text-xs">{l.ipAddress}</span>}
                        </div>
                        <span className="text-[#94a3b8] text-xs">{relativeTime(l.attemptedAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Billable activity */}
              {summary.activityBreakdown && summary.activityBreakdown.some(b => b.billableCount > 0) && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8] mb-2">Billable Activity ({period.label})</h3>
                  <div className="rounded-xl border border-black/[0.08] overflow-hidden divide-y divide-black/[0.05]">
                    {summary.activityBreakdown.filter(b => b.billableCount > 0).map((b, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-[#0f172a] font-medium">{b.service}</span>
                        <span className="text-[#0f172a] font-semibold">{b.billableCount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Non-billable activity */}
              {summary.activityBreakdown && summary.activityBreakdown.some(b => b.count - b.billableCount > 0) && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8] mb-2">Non-Billable Activity ({period.label})</h3>
                  <div className="rounded-xl border border-black/[0.08] overflow-hidden divide-y divide-black/[0.05]">
                    {summary.activityBreakdown.filter(b => b.count - b.billableCount > 0).map((b, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-[#475569]">{b.service}</span>
                        <span className="text-[#475569]">{(b.count - b.billableCount).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent billable lookups */}
              {summary.recentRequests && summary.recentRequests.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8] mb-2">Recent Lookups</h3>
                  <div className="rounded-xl border border-black/[0.08] overflow-hidden divide-y divide-black/[0.05]">
                    {summary.recentRequests.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="text-[#0f172a]">{r.service}</span>
                        <span className="text-[#94a3b8] text-xs">{relativeTime(r.occurredAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent account changes */}
              {summary.recentChanges.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#94a3b8] mb-2">Recent Account Changes</h3>
                  <div className="rounded-xl border border-black/[0.08] overflow-hidden divide-y divide-black/[0.05]">
                    {summary.recentChanges.map((c) => (
                      <div key={c.id} className="px-4 py-2.5 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-[#0f172a]">{c.changeType}</span>
                          <span className="text-[#94a3b8] text-xs">{relativeTime(c.changedAt)}</span>
                        </div>
                        <p className="text-[#64748b] text-xs mt-0.5">{c.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Main page
// --------------------------------------------------------------------------

const UserActivity: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = user?.isSystemAdmin === true;

  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate('/login');
    else if (!isSystemAdmin) navigate('/dashboard');
  }, [user, authLoading, navigate, isSystemAdmin]);

  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState<SortField>('lastLogin');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedRow, setSelectedRow] = useState<AdminUserRow | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(PERIOD_OPTIONS[0]);

  const load = useCallback(async (period: PeriodOption) => {
    setIsLoading(true);
    setError('');
    try {
      setRows(await getAllUsersAdmin(period.startDate, period.endDate));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (isSystemAdmin) load(selectedPeriod); }, [isSystemAdmin, load, selectedPeriod]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'lastLogin' || field === 'apiRequests' ? 'desc' : 'asc');
    }
  };

  const filtered = rows
    .filter((r) => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        r.email.toLowerCase().includes(q) ||
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(q) ||
        (r.clientName ?? '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
      else if (sortField === 'client') cmp = (a.clientName ?? '').localeCompare(b.clientName ?? '');
      else if (sortField === 'lastLogin') {
        const ta = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
        const tb = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
        cmp = ta - tb;
      } else if (sortField === 'apiRequests') cmp = a.apiRequestsLast30Days - b.apiRequestsLast30Days;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="w-3.5 h-3.5 text-[#cbd5e1]" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3.5 h-3.5 text-[#c41e3a]" />
      : <ChevronDown className="w-3.5 h-3.5 text-[#c41e3a]" />;
  };

  const ColHeader = ({ field, label }: { field: SortField; label: string }) => (
    <th
      className="px-4 py-3 text-left cursor-pointer select-none hover:bg-black/[0.02] transition-colors"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#64748b]">
        {label} <SortIcon field={field} />
      </span>
    </th>
  );

  const activeCount = rows.filter((r) => r.isActive).length;
  const neverLogged = rows.filter((r) => !r.lastLoginAt).length;

  return (
    <div className="min-h-screen bg-[#f1f5f9] flex flex-col">
      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <main className="flex-1 px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#c41e3a]/10 rounded-xl">
              <Activity className="w-5 h-5 text-[#c41e3a]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0f172a]">User Activity</h1>
              <p className="text-sm text-[#64748b]">
                {rows.length} users · {activeCount} active · {neverLogged} never logged in
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8] pointer-events-none" />
              <select
                value={selectedPeriod.label}
                onChange={(e) => {
                  const p = PERIOD_OPTIONS.find((o) => o.label === e.target.value)!;
                  setSelectedPeriod(p);
                }}
                className="pl-9 pr-8 py-2 rounded-xl border border-black/[0.08] bg-white text-sm text-[#475569] font-medium focus:outline-none focus:border-[#c41e3a]/50 focus:ring-2 focus:ring-[#c41e3a]/10 appearance-none cursor-pointer"
              >
                {PERIOD_OPTIONS.map((o) => (
                  <option key={o.label} value={o.label}>{o.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#94a3b8] pointer-events-none" />
            </div>
            <button
              onClick={() => load(selectedPeriod)}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-black/[0.08] text-sm font-medium text-[#475569] hover:bg-[#f8fafc] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-5">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, email, or company…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-black/[0.08] bg-white text-sm focus:outline-none focus:border-[#c41e3a]/50 focus:ring-2 focus:ring-[#c41e3a]/10"
          />
          {filter && (
            <button onClick={() => setFilter('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-[#94a3b8]" />
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-[#94a3b8]">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading users…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2">
              <Users className="w-8 h-8 text-[#cbd5e1]" />
              <p className="text-sm text-[#94a3b8]">{filter ? 'No users match that search.' : 'No users found.'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead className="bg-[#f8fafc] border-b border-black/[0.08]">
                  <tr>
                    <ColHeader field="name" label="User" />
                    <ColHeader field="client" label="Client" />
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748b]">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[#64748b]">Status</th>
                    <ColHeader field="lastLogin" label="Last Login" />
                    <ColHeader field="apiRequests" label={`Billable Lookups (${selectedPeriod.label})`} />
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-black/[0.05] hover:bg-[#fafbfc] transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#0f172a] text-sm">{row.firstName} {row.lastName}</div>
                        <div className="text-xs text-[#64748b]">{row.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-[#64748b]">
                        {row.clientName ?? <span className="text-[#cbd5e1]">—</span>}
                      </td>
                      <td className="px-4 py-3">{roleBadge(row)}</td>
                      <td className="px-4 py-3">
                        {row.isActive
                          ? <span className="flex items-center gap-1 text-xs text-green-700"><CheckCircle className="w-3.5 h-3.5" />Active</span>
                          : <span className="flex items-center gap-1 text-xs text-[#94a3b8]"><XCircle className="w-3.5 h-3.5" />Inactive</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-[#0f172a]">{relativeTime(row.lastLoginAt)}</div>
                        {row.lastLoginAt && (
                          <div className="text-xs text-[#94a3b8]">
                            {new Date(row.lastLoginAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${row.apiRequestsLast30Days > 0 ? 'text-[#0f172a]' : 'text-[#cbd5e1]'}`}>
                          {row.apiRequestsLast30Days.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelectedRow(row)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#475569] transition-colors"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Row count */}
        {!isLoading && filtered.length > 0 && (
          <p className="text-xs text-[#94a3b8] mt-3 text-right">
            Showing {filtered.length} of {rows.length} users
          </p>
        )}
      </main>

      <Footer />

      {selectedRow && (
        <ActivityModal row={selectedRow} period={selectedPeriod} onClose={() => setSelectedRow(null)} />
      )}
    </div>
  );
};

export default UserActivity;
